/* eslint-disable */
import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Input, Table } from 'reactstrap';
import toast from 'react-hot-toast';
import { walletService, customerService } from '../services/api';
import * as Icons from 'lucide-react';
import { formatCurrencyCOP } from '../utils/formatters';

/**
 * AbonoParcialesDialog
 *
 * Modal similar a MultiInvoiceRCDialog pero con lógica de abono parcial:
 *  - Step 1: Buscar y seleccionar cliente (auto-carga facturas)
 *  - Step 2: Panel izquierdo con facturas, panel derecho con detalle de pago
 *  - Solo una factura puede quedar con pago parcial
 *  - Crea RC en Siigo y actualiza paid_amount en BD
 */
const AbonoParcialesDialog = ({ isOpen, toggle, onSuccess }) => {
    const [step, setStep] = useState(1); // 1: Cliente, 2: Facturas + Pago
    const [loading, setLoading] = useState(false);

    // Step 1 - Customer
    const [customerSearch, setCustomerSearch] = useState('');
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    // Step 2 - Payment details
    const [totalAmount, setTotalAmount] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('transferencia');
    const [siigoPaymentId, setSiigoPaymentId] = useState('7476');
    const [notes, setNotes] = useState('');
    const [files, setFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);

    // Orders
    const [pendingOrders, setPendingOrders] = useState([]);
    const [selectedOrders, setSelectedOrders] = useState(new Map());

    // Bank reconciliation
    const [linkedMovements, setLinkedMovements] = useState([]);
    const [movementSearch, setMovementSearch] = useState('');
    const [foundMovements, setFoundMovements] = useState([]);
    const [loadingMovements, setLoadingMovements] = useState(false);

    // Reset on close
    useEffect(() => {
        if (!isOpen) {
            setStep(1);
            setCustomerSearch('');
            setCustomers([]);
            setSelectedCustomer(null);
            setTotalAmount('');
            setPaymentMethod('transferencia');
            setSiigoPaymentId('7476');
            setNotes('');
            setFiles([]);
            setPendingOrders([]);
            setSelectedOrders(new Map());
            setLinkedMovements([]);
            setMovementSearch('');
            setFoundMovements([]);
        }
    }, [isOpen]);

    // ── Computed ──
    const parsedTotal = parseFloat(totalAmount) || 0;
    const applied = Array.from(selectedOrders.values()).reduce((s, v) => s + v, 0);
    const remaining = Math.max(0, parsedTotal - applied);

    // ── File handling ──
    const handleFileChange = (e) => {
        if (e.target.files) setFiles(prev => [...prev, ...Array.from(e.target.files)].slice(0, 5));
    };
    const removeFile = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));

    const handlePaste = useCallback((e) => {
        const items = e.clipboardData?.items || [];
        const imgFiles = [];
        for (const item of items) {
            if (item.type.startsWith('image/')) {
                const f = item.getAsFile();
                if (f) imgFiles.push(new File([f], `comprobante_${Date.now()}.${f.type.split('/')[1] || 'png'}`, { type: f.type }));
            }
        }
        if (imgFiles.length) { e.preventDefault(); setFiles(prev => [...prev, ...imgFiles].slice(0, 5)); }
    }, []);

    // ── Customer search ──
    const handleCustomerSearch = async (term) => {
        setCustomerSearch(term);
        if (term.length < 3) { setCustomers([]); return; }
        try {
            const res = await customerService.searchCustomers(term);
            setCustomers(res.customers || []);
        } catch { toast.error('Error buscando clientes'); }
    };

    const handleSelectCustomer = async (customer) => {
        setSelectedCustomer(customer);
        setCustomerSearch(customer.name || customer.commercial_name || 'Cliente');
        setCustomers([]);
        // Auto-load pending orders
        try {
            setLoading(true);
            const res = await walletService.getPendingOrdersForAbono(customer.id);
            const orders = res.data || [];
            setPendingOrders(orders);
            setSelectedOrders(new Map());
            if (!orders.length) {
                toast('Este cliente no tiene facturas pendientes', { icon: 'ℹ️' });
            } else {
                setStep(2);
            }
        } catch { toast.error('Error cargando facturas'); }
        finally { setLoading(false); }
    };

    // ── Selection logic ──
    const handleToggleOrder = (order) => {
        const newMap = new Map(selectedOrders);
        if (newMap.has(order.id)) {
            newMap.delete(order.id);
        } else {
            if (remaining <= 0) return; // Budget exhausted
            const balance = parseFloat(order.balance);
            const toApply = Math.min(balance, remaining);
            newMap.set(order.id, Math.round(toApply * 100) / 100);
        }
        setSelectedOrders(newMap);
    };

    const isPartial = (order) => {
        if (!selectedOrders.has(order.id)) return false;
        const appliedAmt = selectedOrders.get(order.id);
        const balance = parseFloat(order.balance);
        return appliedAmt < balance - 1;
    };

    // ── Bancolombia movements ──
    const handleSearchMovements = async (q) => {
        setMovementSearch(q);
        if (!q || q.length < 3) { setFoundMovements([]); return; }
        try {
            setLoadingMovements(true);
            const res = await walletService.getBancolombiaMovements({ status: 'pending', search: q, limit: 20 });
            const results = Array.isArray(res) ? res : (res.data || res.movements || []);
            const linkedIds = linkedMovements.map(m => m.id);
            setFoundMovements(results.filter(m => !linkedIds.includes(m.id)));
        } catch { /* silent */ }
        finally { setLoadingMovements(false); }
    };

    const handleLinkMovement = (movement) => {
        setLinkedMovements(prev => [...prev, movement]);
        setFoundMovements([]);
        setMovementSearch('');
    };

    const handleUnlinkMovement = (id) => {
        setLinkedMovements(prev => prev.filter(m => m.id !== id));
    };

    const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-CO') : '';

    // ── Submit ──
    const handleSubmit = async () => {
        if (selectedOrders.size === 0) { toast.error('Selecciona al menos una factura'); return; }
        if (!parsedTotal || parsedTotal <= 0) { toast.error('Ingresa un monto válido'); return; }

        const orderPayments = Array.from(selectedOrders.entries()).map(([orderId, amount]) => ({ orderId, amount }));

        const formData = new FormData();
        formData.append('totalAmount', parsedTotal);
        formData.append('paymentMethod', paymentMethod);
        formData.append('siigoPaymentId', siigoPaymentId);
        formData.append('notes', notes);
        formData.append('orderPayments', JSON.stringify(orderPayments));
        if (linkedMovements.length > 0) formData.append('linkedMovementIds', JSON.stringify(linkedMovements.map(m => m.id)));
        files.forEach(f => formData.append('files', f));

        try {
            setLoading(true);
            const res = await walletService.createAbonoParcel(formData);
            if (res.success) {
                if (res.siigoDeferred) {
                    toast(res.message || 'Abono guardado. RC pendiente por Siigo.', { icon: '⚠️', duration: 8000 });
                } else {
                    toast.success(res.message || 'Abono creado exitosamente');
                    if (res.data?.link) {
                        toast.success(
                            <div>
                                <div>{res.data.rcName}</div>
                                <a href={res.data.link} target="_blank" rel="noopener noreferrer" className="underline">Ver RC en Siigo</a>
                            </div>,
                            { duration: 8000 }
                        );
                    }
                }
                if (onSuccess) onSuccess(res.data);
                toggle();
            }
        } catch (err) {
            const status = err.response?.status;
            if (status === 503 && err.response?.data?.siigo_unavailable) {
                toast('⚠️ Siigo no está disponible. Intenta de nuevo en unos minutos.', { icon: '⏳', duration: 6000 });
            } else {
                const msg = err.response?.data?.message || 'Error creando el abono';
                toast.error(msg);
            }
        } finally { setLoading(false); }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl" className="modal-dialog-centered modal-xl-custom">
            <ModalHeader toggle={toggle}>
                Abono Parcial {step === 1 && '- Seleccionar Cliente'}
                {step === 2 && '- Seleccionar Pedidos y Distribuir Pago'}
            </ModalHeader>

            <ModalBody className="p-0">
                {/* ── STEP 1: Customer Selection ── */}
                {step === 1 && (
                    <div className="p-4">
                        <FormGroup>
                            <Label>Buscar Cliente</Label>
                            <div className="relative">
                                <Icons.Search className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
                                <Input
                                    type="text"
                                    placeholder="Nombre, NIT o identificación..."
                                    value={customerSearch}
                                    onChange={(e) => handleCustomerSearch(e.target.value)}
                                    autoFocus
                                    className="pl-10"
                                />
                            </div>
                        </FormGroup>

                        {customers.length > 0 && (
                            <div className="border rounded mt-2 max-h-64 overflow-y-auto bg-white shadow-sm">
                                <Table hover size="sm" className="mb-0">
                                    <tbody>
                                        {customers.map((customer) => (
                                            <tr
                                                key={customer.id}
                                                onClick={() => handleSelectCustomer(customer)}
                                                className="cursor-pointer"
                                            >
                                                <td className="p-3">
                                                    <div className="font-bold text-gray-800">
                                                        {customer.name || customer.commercial_name || 'Sin Nombre'}
                                                    </div>
                                                    <div className="text-xs text-gray-500 flex items-center gap-2">
                                                        <span className="bg-gray-100 px-2 py-0.5 rounded text-gray-600">
                                                            {customer.identification}
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}

                        {loading && <div className="text-center py-8"><Icons.Loader className="animate-spin mx-auto text-blue-500" /></div>}

                        {!loading && customers.length === 0 && customerSearch.length >= 3 && (
                            <div className="text-center py-8 text-gray-400">
                                <Icons.UserX className="mx-auto w-8 h-8 mb-2 opacity-50" />
                                <p>No se encontraron clientes</p>
                            </div>
                        )}
                    </div>
                )}

                {/* ── STEP 2: Orders + Payment (Split Layout) ── */}
                {step === 2 && (
                    <div className="flex flex-col h-[70vh]">
                        {/* Header */}
                        <div className="px-4 py-3 bg-gray-50 border-b flex justify-between items-center shrink-0">
                            <div>
                                <div className="text-xs text-gray-500 uppercase font-bold">Cliente</div>
                                <div className="font-bold text-gray-800">{selectedCustomer?.name || selectedCustomer?.commercial_name}</div>
                            </div>
                            <button
                                onClick={() => setStep(1)}
                                className="text-xs text-blue-600 hover:text-blue-800 flex items-center font-medium bg-white px-3 py-1.5 rounded border border-blue-200 hover:bg-blue-50 transition-colors"
                            >
                                <Icons.ArrowLeft className="w-3 h-3 mr-1" /> Cambiar
                            </button>
                        </div>

                        <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
                            {/* ── Left Pane: Orders ── */}
                            <div className="w-full md:w-7/12 p-4 overflow-y-auto border-r border-gray-100 custom-scrollbar">
                                <h4 className="text-sm font-bold text-gray-700 uppercase mb-3 flex items-center">
                                    <Icons.ShoppingCart className="w-4 h-4 mr-2" />
                                    Pedidos Pendientes
                                </h4>
                                <Table size="sm" hover className="mb-0">
                                    <thead className="bg-white sticky top-0 z-10">
                                        <tr>
                                            <th className="w-8 sticky top-0 bg-white"></th>
                                            <th className="text-xs font-bold text-gray-500 uppercase sticky top-0 bg-white">Pedido</th>
                                            <th className="text-xs font-bold text-gray-500 uppercase text-right sticky top-0 bg-white">Saldo</th>
                                            <th className="text-xs font-bold text-gray-500 uppercase text-right sticky top-0 bg-white">Aplicar</th>
                                            <th className="text-xs font-bold text-gray-500 uppercase text-center sticky top-0 bg-white">Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {pendingOrders.map(order => {
                                            const isSelected = selectedOrders.has(order.id);
                                            const isPartialPay = isPartial(order);
                                            const canSelect = isSelected || remaining > 0;
                                            const balance = parseFloat(order.balance);

                                            return (
                                                <tr key={order.id} className={`${isSelected ? 'bg-amber-50/40' : ''} ${!canSelect ? 'opacity-40' : ''}`}>
                                                    <td className="align-middle">
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            disabled={!canSelect}
                                                            onChange={() => handleToggleOrder(order)}
                                                            className="w-4 h-4 rounded text-amber-500 focus:ring-amber-400 disabled:cursor-not-allowed"
                                                        />
                                                    </td>
                                                    <td className="align-middle">
                                                        <div className="font-bold text-sm text-gray-800">{order.order_number}</div>
                                                        <div className="text-[10px] text-gray-500">{formatDate(order.date)}</div>
                                                    </td>
                                                    <td className="align-middle text-right text-xs font-medium text-gray-600">
                                                        {formatCurrencyCOP(balance)}
                                                    </td>
                                                    <td className="align-middle text-right">
                                                        {isSelected ? (
                                                            <span className="font-bold text-sm text-amber-700">
                                                                {formatCurrencyCOP(selectedOrders.get(order.id))}
                                                            </span>
                                                        ) : (
                                                            <span className="text-gray-300 text-xs">—</span>
                                                        )}
                                                    </td>
                                                    <td className="align-middle text-center">
                                                        {isSelected && (
                                                            isPartialPay ? (
                                                                <span className="text-[10px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-bold border border-orange-200">PARCIAL</span>
                                                            ) : (
                                                                <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold border border-green-200">COMPLETO</span>
                                                            )
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot className="bg-gray-50 border-t border-gray-200 sticky bottom-0">
                                        <tr>
                                            <td colSpan="3" className="text-right text-xs font-bold text-gray-600 py-3">Total Aplicado:</td>
                                            <td className="text-right text-sm font-black text-amber-700 py-3 px-2">
                                                {formatCurrencyCOP(applied)}
                                            </td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                </Table>
                            </div>

                            {/* ── Right Pane: Payment Details ── */}
                            <div className="w-full md:w-5/12 p-4 bg-gray-50/50 overflow-y-auto custom-scrollbar">
                                <h4 className="text-sm font-bold text-gray-700 uppercase mb-3 flex items-center">
                                    <Icons.CreditCard className="w-4 h-4 mr-2" />
                                    Detalle del Pago
                                </h4>

                                <div className="space-y-4">
                                    {/* Monto total */}
                                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                                        <label className="text-xs font-bold text-amber-700 uppercase mb-1 block flex items-center gap-1">
                                            <Icons.Coins className="w-3.5 h-3.5" /> Monto Total Pagado
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">$</span>
                                            <input
                                                type="number"
                                                placeholder="0"
                                                value={totalAmount}
                                                onChange={e => setTotalAmount(e.target.value)}
                                                className="w-full pl-7 pr-4 py-2 text-lg font-bold border-2 border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent bg-white"
                                            />
                                        </div>
                                        {parsedTotal > 0 && (
                                            <div className="flex justify-between mt-1">
                                                <span className="text-xs text-amber-600">{formatCurrencyCOP(parsedTotal)}</span>
                                                <span className={`text-xs font-bold ${remaining <= 0 ? 'text-green-600' : 'text-amber-600'}`}>
                                                    Disponible: {formatCurrencyCOP(remaining)}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Método de pago */}
                                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                                        <FormGroup className="mb-0">
                                            <Label className="text-xs font-bold text-gray-500 uppercase mb-1">Método de Pago</Label>
                                            <Input
                                                type="select"
                                                value={siigoPaymentId}
                                                onChange={(e) => {
                                                    const id = e.target.value;
                                                    setSiigoPaymentId(id);
                                                    if (id === '7476') setPaymentMethod('transferencia');
                                                    else if (id === '5671') setPaymentMethod('efectivo');
                                                    else if (id === '11136') setPaymentMethod('bold');
                                                    else setPaymentMethod('otros');
                                                }}
                                            >
                                                <option value="7476">Consignación Bancaria / Transferencia (7476)</option>
                                                <option value="5671">Efectivo (5671)</option>
                                                <option value="11136">Bold / Datáfono (11136)</option>
                                            </Input>
                                        </FormGroup>
                                    </div>

                                    {/* Comprobante */}
                                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200" onPaste={handlePaste} tabIndex={0}>
                                        <label className="text-xs font-bold text-gray-500 uppercase mb-2 block">Comprobante de Pago</label>
                                        <label
                                            htmlFor="abono-upload"
                                            className={`flex flex-col items-center justify-center w-full h-16 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${isDragging ? 'border-amber-500 bg-amber-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'}`}
                                            onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                                            onDragLeave={e => { e.preventDefault(); setIsDragging(false); }}
                                            onDrop={e => { e.preventDefault(); setIsDragging(false); if (e.dataTransfer.files?.length) setFiles(prev => [...prev, ...Array.from(e.dataTransfer.files)].slice(0, 5)); }}
                                        >
                                            <Icons.UploadCloud className={`w-5 h-5 mb-1 ${isDragging ? 'text-amber-500' : 'text-gray-400'}`} />
                                            <p className="text-xs text-gray-500"><span className="font-semibold">Clic para subir</span>, arrastrar o Ctrl+V</p>
                                            <p className="text-[10px] text-gray-400">IMG, PDF (Max. 5MB)</p>
                                            <input id="abono-upload" type="file" className="hidden" multiple accept="image/*,application/pdf" onChange={handleFileChange} />
                                        </label>
                                        {files.length > 0 && (
                                            <div className="mt-2 space-y-1">
                                                {files.map((f, i) => (
                                                    <div key={i} className="flex items-center justify-between p-1.5 bg-amber-50 rounded border border-amber-100">
                                                        <div className="flex items-center gap-1.5 overflow-hidden">
                                                            <Icons.FileText className="w-3 h-3 text-amber-600 shrink-0" />
                                                            <span className="text-[10px] text-gray-700 truncate max-w-[150px]">{f.name}</span>
                                                        </div>
                                                        <button onClick={() => removeFile(i)} className="text-gray-400 hover:text-red-500"><Icons.X className="w-3 h-3" /></button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Conciliación Bancaria */}
                                    <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-100">
                                        <div className="flex justify-between items-center mb-2">
                                            <label className="text-xs font-bold text-indigo-700 uppercase flex items-center mb-0">
                                                <Icons.Link className="w-3 h-3 mr-1.5" /> Conciliación Bancaria
                                            </label>
                                            {linkedMovements.length > 0 && (
                                                <span className="bg-emerald-100 text-emerald-700 text-[10px] px-2 py-0.5 rounded-full font-bold border border-emerald-200">
                                                    <Icons.CheckCircle2 className="w-3 h-3 mr-1 inline" /> {linkedMovements.length}
                                                </span>
                                            )}
                                        </div>
                                        {linkedMovements.length > 0 && (
                                            <div className="space-y-1.5 mb-2">
                                                {linkedMovements.map(lm => (
                                                    <div key={lm.id} className="bg-white p-2 rounded-lg border border-emerald-200 flex justify-between items-center">
                                                        <div className="text-sm font-bold text-gray-800">{formatCurrencyCOP(lm.valor)}</div>
                                                        <div className="text-[10px] text-gray-500 truncate max-w-[100px]">{lm.descripcion}</div>
                                                        <button onClick={() => handleUnlinkMovement(lm.id)} className="text-gray-400 hover:text-red-500">
                                                            <Icons.X className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <div className="relative">
                                            <Icons.Search className="absolute left-3 top-2.5 text-gray-400 w-3.5 h-3.5" />
                                            <input
                                                type="text"
                                                placeholder="Buscar movimiento (valor, fecha)..."
                                                value={movementSearch}
                                                onChange={e => handleSearchMovements(e.target.value)}
                                                className="w-full pl-9 pr-3 py-2 text-sm border border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-400 bg-white"
                                            />
                                            {loadingMovements && <Icons.Loader className="absolute right-3 top-2.5 w-3.5 h-3.5 animate-spin text-gray-400" />}
                                            {foundMovements.length > 0 && (
                                                <div className="mt-1 max-h-40 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg absolute z-50 w-full left-0 top-full">
                                                    {foundMovements.map(mov => (
                                                        <div
                                                            key={mov.id}
                                                            onClick={() => handleLinkMovement(mov)}
                                                            className="p-2 border-b last:border-0 hover:bg-indigo-50 cursor-pointer"
                                                        >
                                                            <div className="flex justify-between items-center">
                                                                <span className="font-bold text-sm">{formatCurrencyCOP(mov.valor)}</span>
                                                                <span className="text-[10px] text-gray-400">{formatDate(mov.fecha)}</span>
                                                            </div>
                                                            <div className="text-xs text-gray-500 truncate">{mov.descripcion}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Notas */}
                                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                                        <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Notas (Opcional)</label>
                                        <textarea
                                            rows={2}
                                            placeholder="Observaciones..."
                                            value={notes}
                                            onChange={e => setNotes(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </ModalBody>

            <ModalFooter className="bg-gray-50 border-t border-gray-200">
                {step === 2 && (
                    <>
                        <div className="mr-auto text-sm text-gray-500">
                            <span className="font-bold text-gray-700">{selectedOrders.size}</span> factura(s) — <span className="font-bold text-amber-700">{formatCurrencyCOP(applied)}</span>
                        </div>
                        <Button color="white" className="border border-gray-300 text-gray-700 hover:bg-gray-50" onClick={() => setStep(1)} disabled={loading}>
                            Atrás
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={loading || selectedOrders.size === 0 || parsedTotal <= 0}
                            className="bg-amber-500 border-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-200"
                        >
                            {loading
                                ? <><Icons.Loader className="animate-spin mr-2 inline" size={16} />Creando...</>
                                : <><Icons.CheckCircle className="mr-2 inline" size={16} />Crear Abono ({selectedOrders.size})</>
                            }
                        </Button>
                    </>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default AbonoParcialesDialog;
